<script setup lang="ts">
  import { storeToRefs } from 'pinia';
  import { computed, inject, ref, watch, watchEffect, type Ref } from 'vue';
  import { useRoute, type RouteLocationRaw, type RouteMeta } from 'vue-router';
  import { useDisplay } from 'vuetify';

  import { EnumRoutes } from '@/helpers/enums/routes.enum';
  import { useI18 } from '@/plugins';
  import { useAuthStore } from '@/stores/auth.store';
  import { useGroupStore } from '@/stores/groups.store';
  import { useLocaleStore } from '@/stores/locale.store';
  import { useOrganizationStore } from '@/stores/organzation.pinia';
  import { usePermissionsStore } from '@/stores/permissoins.store';
  import type { headerItem } from '@/submodules/himam-common-components/src/helpers/interfaces/header-item.interface';

  const props = defineProps<{
    modelValue: boolean;
    currentWidth?: number;
  }>();
  const emit = defineEmits<{
    (event: 'update:modelValue', value: boolean): void;
    (event: 'update:currentWidth', value: number): void;
  }>();

  const { smAndDown } = useDisplay();
  // Read direction from the locale store directly — BasicLayout doesn't
  // provide('isLTR'), so inject() would fall back to LTR and pin the drawer to
  // the left even in Arabic.
  const { isLTR } = storeToRefs(useLocaleStore());
  const paddingTop: Ref<number> = inject('paddingTop', ref(0));

  const orgStore = useOrganizationStore();
  const { dashboardText, coursesText } = storeToRefs(orgStore);
  const permissionsStore = usePermissionsStore();
  const { checkAuthentication } = permissionsStore;
  const { t } = useI18();
  const authStore = useAuthStore();
  const { user } = storeToRefs(authStore);
  const groupStore = useGroupStore();
  const { groups } = storeToRefs(groupStore);

  const isExpanded = ref(true);

  const EXPANDED_WIDTH = 280;
  const COLLAPSED_WIDTH = 90;

  const sideNavItems = computed(() => {
    // "Explore courses" heads the nav (per Figma), gated by the same
    // browse-permission check as the app-bar All Courses link.
    const exploreItem: headerItem = {
      title: t('header.exploreCourses', {
        defaultValue: 'Explore courses',
      }).toString(),
      icon: 'mdi-compass-outline',
      to: EnumRoutes.lms.allCourses.path,
      routeName: EnumRoutes.lms.allCourses.name,
    };

    const authItems = [
      {
        ...EnumRoutes.dashboard.root,
        title: dashboardText.value,
        icon: 'mdi-view-dashboard-outline',
      },
      {
        ...EnumRoutes.lms.myCourses,
        title: 'course.myCourses',
        icon: 'mdi-school-outline',
      },
      {
        ...EnumRoutes.profile.myGrades.root,
        // Pre-resolved so it renders before the CMS has the key (the reduce's
        // t() on a non-key string returns it unchanged).
        title: t('grades.gradesAndProgress', 'Grades & Progress').toString(),
        icon: 'mdi-chart-line',
      },
      {
        ...EnumRoutes.dashboard.userActivities,
        title: 'dashboard.schedule',
        icon: 'mdi-calendar-outline',
      },
    ];

    const items = authItems.reduce((acc: headerItem[], item) => {
      if (checkAuthentication(item.meta as RouteMeta))
        if (
          !(item.meta as RouteMeta).shouldHaveGroup ||
          groups.value?.data?.length
        ) {
          acc.push({
            title: (item.meta as RouteMeta).dontUseKey
              ? item.title
              : t(item.title, { courseText: coursesText.value }).toString(),
            icon: item.icon,
            to: item.path,
            routeName: item.name,
          });
        }
      return acc;
    }, []);

    const showExplore = checkAuthentication(
      EnumRoutes.lms.allCourses.meta as RouteMeta
    );
    return [...(showExplore ? [exploreItem] : []), ...items];
  });

  // Active route tracking
  const route = useRoute();
  const path: Ref<string> = inject('path', ref(''));
  const activeItem: Ref<RouteLocationRaw[]> = ref([]);
  const activeRouteName = computed(() => route.meta.activeRouteName);

  watchEffect(() => {
    let firstMatch = '';
    sideNavItems.value.forEach((item) => {
      if (item.routeName === activeRouteName.value) {
        firstMatch = item.to as string;
        activeItem.value = [item.to as string];
        return;
      }
      if (
        path.value.startsWith(item.to as string) &&
        (item.to as string)?.length >= firstMatch.length
      ) {
        firstMatch = item.to as string;
        activeItem.value = [item.to as string];
      }
    });
    if (!firstMatch.length) activeItem.value = [];
  });

  const scrollUp = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const activeWidth = computed(() => {
    if (smAndDown.value) return 280;
    return isExpanded.value ? EXPANDED_WIDTH : COLLAPSED_WIDTH;
  });

  watch(
    activeWidth,
    (val) => {
      emit('update:currentWidth', val);
    },
    { immediate: true }
  );

  const isTemporary = computed(() => smAndDown.value);
  const isPermanent = computed(() => !smAndDown.value);

  const drawerModelValue = computed({
    get: () => (smAndDown.value ? props.modelValue : true),
    set: (val: boolean) => emit('update:modelValue', val),
  });

  const toggleExpanded = () => {
    isExpanded.value = !isExpanded.value;
  };
</script>

<template>
  <v-navigation-drawer
    v-model="drawerModelValue"
    :permanent="isPermanent"
    :temporary="isTemporary"
    :width="activeWidth"
    class="auth-side-nav"
    :style="
      !smAndDown
        ? { top: paddingTop + 'px', height: `calc(100vh - ${paddingTop}px)` }
        : { top: paddingTop + 'px' }
    "
    location="start"
    :floating="!smAndDown"
    elevation="0"
  >
    <!-- ======= EXPANDED MODE ======= -->
    <template v-if="isExpanded || smAndDown">
      <!-- Top row: "Explore courses" link + collapse arrow -->
      <div
        class="d-flex align-center justify-space-between pt-4 pb-3"
        style="
          padding-inline: 14px;
          border-bottom: thin solid
            rgba(var(--v-border-color), var(--v-border-opacity));
        "
      >
        <RouterLink
          v-if="sideNavItems.length"
          :to="sideNavItems[0].to ?? ''"
          class="text-decoration-none d-flex align-center ga-2"
          :class="
            activeItem?.[0] === sideNavItems[0].to
              ? 'text-primary'
              : 'text-grey-darken-1'
          "
          @click="scrollUp"
        >
          <v-icon :icon="sideNavItems[0].icon" size="18" />
          <span class="text-body-2 text-capitalize">{{
            sideNavItems[0].title
          }}</span>
        </RouterLink>
        <v-btn
          v-if="!smAndDown"
          size="28"
          variant="text"
          color="grey-darken-2"
          :icon="isLTR ? 'mdi-arrow-collapse-left' : 'mdi-arrow-collapse-right'"
          aria-label="Collapse sidebar"
          @click="toggleExpanded"
        />
      </div>

      <!-- Nav items with icon + text (skip first item, already shown above) -->
      <nav class="expanded-nav">
        <RouterLink
          v-for="item in sideNavItems.slice(1)"
          :key="item.routeName"
          :to="item.to ?? ''"
          class="expanded-nav-item text-decoration-none d-flex align-center ga-3"
          :class="
            activeItem?.[0] === item.to
              ? 'active text-primary'
              : 'text-grey-darken-1'
          "
          @click="scrollUp"
        >
          <v-icon :icon="item.icon" size="18" />
          <span class="text-body-2 text-capitalize">{{ item.title }}</span>
        </RouterLink>
      </nav>
    </template>

    <!-- ======= COLLAPSED MODE (icon + label stacked) ======= -->
    <template v-else>
      <div class="d-flex justify-center pt-3 pb-1">
        <v-btn
          size="32"
          variant="outlined"
          density="comfortable"
          color="grey-darken-1"
          :icon="isLTR ? 'mdi-chevron-double-right' : 'mdi-chevron-double-left'"
          aria-label="Expand sidebar"
          class="toggle-btn"
          @click="toggleExpanded"
        />
      </div>

      <nav class="collapsed-nav mt-1">
        <RouterLink
          v-for="item in sideNavItems"
          :key="item.routeName"
          :to="item.to ?? ''"
          class="collapsed-nav-item text-decoration-none"
          :class="
            activeItem?.[0] === item.to ? 'text-primary active' : 'text-gray'
          "
          @click="scrollUp"
        >
          <v-icon :icon="item.icon" size="22" />
          <span class="collapsed-nav-label">{{ item.title }}</span>
        </RouterLink>
      </nav>
    </template>

    <!-- ======= HELP (bottom) ======= -->
    <!-- Hide it temporarily -->
    <template v-if="false" #append>
      <div v-if="isExpanded || smAndDown" class="expanded-nav pb-4">
        <div
          class="expanded-nav-item text-grey-darken-1 d-flex align-center ga-3 cursor-pointer"
        >
          <v-icon icon="mdi-help-circle-outline" size="18" />
          <span class="text-body-2 text-capitalize">{{
            t('shared.help')
          }}</span>
        </div>
      </div>
      <div v-else class="collapsed-nav pb-3">
        <div class="collapsed-nav-item text-gray">
          <v-icon icon="mdi-help-circle-outline" size="22" />
          <span class="collapsed-nav-label">{{ t('shared.help') }}</span>
        </div>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<style lang="scss">
  .auth-side-nav {
    position: fixed !important;
    z-index: 1004;
    border-inline-end: thin solid
      rgba(var(--v-border-color), var(--v-border-opacity));
    transition: width 0.2s ease;

    .toggle-btn {
      border-color: rgba(var(--v-border-color), 0.3);
    }

    // ===== Expanded nav =====
    .expanded-nav {
      padding: 20px 0;

      .expanded-nav-item {
        padding: 12px 14px;
        margin-bottom: 4px;
        border-inline-start: 3px solid transparent;
        transition: background-color 0.15s ease;
        cursor: pointer;

        &:hover {
          background-color: rgba(0, 0, 0, 0.03);
        }

        &.active {
          border-inline-start-color: rgb(var(--v-theme-primary));
          background-color: rgba(var(--v-theme-primary), 0.06);
          font-weight: 500;
        }
      }
    }

    // ===== Collapsed nav =====
    .collapsed-nav {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 4px 8px;

      .collapsed-nav-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
        padding: 10px 6px 8px;
        margin-bottom: 2px;
        border-radius: 8px;
        cursor: pointer;
        transition: background-color 0.15s ease;
        text-align: center;

        &:hover {
          background-color: rgba(0, 0, 0, 0.04);
        }

        &.active {
          background-color: rgba(var(--v-theme-primary), 0.08);
          font-weight: 500;
        }
      }

      .collapsed-nav-label {
        font-size: 11px;
        line-height: 1.3;
        margin-top: 5px;
        text-transform: capitalize;
        word-break: break-word;
        max-width: 100%;
      }
    }

    .v-navigation-drawer__content {
      &::-webkit-scrollbar {
        width: 5px;
      }
      &::-webkit-scrollbar-track {
        background: #f1f1f1;
      }
      &::-webkit-scrollbar-thumb {
        background: #888;
      }
      &::-webkit-scrollbar-thumb:hover {
        background: #555;
      }
      & {
        scrollbar-width: thin;
        scrollbar-color: #888 #f1f1f1;
      }
    }
  }
</style>
